import React, { useEffect, useState } from "react";
import {
    useTheme,
    Grid,
    Dialog,
    DialogContent,
    IconButton,
    DialogTitle,
    Button,
    FormHelperText,
    useMediaQuery,
    Typography,
    Divider,
    Tooltip,
} from "@mui/material";

import { Close, ContentCopy, Language, TaskAltOutlined, TipsAndUpdates } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useSubmitReportGrantMutation } from "Services/ReferralService";

import reportGrantImg from "assets/images/referral/report_grant.svg";
import FormControlWrapper from "components/FormControlWrapper";
import DatePickerWrapper from "components/DatePickerWrapper";
import CheckboxWrapper from "components/CheckboxWrapper";
import referCharityImg from "assets/images/referral/refer_charity.svg";
import { LoadingButton } from "@mui/lab";
import { showErrorToaster, showSuccessToaster } from "store/Shared.Slice";
import { Box } from "@mui/system";

const ReportGrantPopup = ({ openPopup, onClose }) => {

    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { userToken } = useSelector((state) => state.sharedState);

    const matchDown1 = useMediaQuery('(max-height:950px)');
    const matchDown2 = useMediaQuery('(max-height:850px)');

    const [submitReportGrant, { isLoading: isSubmiting }] = useSubmitReportGrantMutation();

    const [ showReferMsg, setShowReferMsg ] = useState(true);
    const [formFields, setFormFields] = useState({
        grantAmount: {
            value: ' ',
            error: ''
        },
        grantPurpose: {
            value: '',
            error: ''
        },
        dateAwarded: {
            value: null,
            error: ''
        },
        consent: {
            value: false,
            error: ''
        },
    });

    const handleUpdate = (field, value) => {
        setFormFields({
            ...formFields,
            [field]: {
                value,
                error: ''
            }
        });
    }

    const handleClear = () => {
         setFormFields({
            ...formFields,
            grantAmount: {
                value: '',
                error: ''
            },
            grantPurpose: {
                value: '',
                error: ''
            },
            dateAwarded: {
                value: null,
                error: ''
            },
            consent: {
                value: false,
                error: ''
            }
        });
    }

    const handleCopyToClipboard = () => {
        const referUrl = `${window.location.origin}/register?referral=${userToken?.UserName}`;

        navigator.clipboard.writeText(referUrl).then(() => {
            dispatch(
                showSuccessToaster(
                    `Copied to clipboard. ${referUrl}`
                )
            );
        });
    };

    const handleSubmit = async() => {
        try {

            const model = {
                grantAmount: formFields.grantAmount.value || null,
                grantPurpose: formFields.grantPurpose.value || null,
                dateAwarded: formFields.dateAwarded.value !== null ? new Date(formFields.dateAwarded.value) : null,
                shareConsent: formFields.consent.value || null,
            };
            
            const response = await submitReportGrant(model).unwrap();

            dispatch(showSuccessToaster(response.message));
            onClose();
        }
        catch(error) {
            // Model Errors
            if (error.data?.errors != null) {
                const copyFormValue = { ...formFields };
                Object.keys(error.data?.errors).forEach((key) => {
                    if (copyFormValue[key]) {
                        copyFormValue[key].error = error.data?.errors[key][0];
                    }
                });

                setFormFields({
                    ...formFields,
                    ...copyFormValue
                });

                dispatch(showErrorToaster(t('errors.model_error')));
            }
            // Bad Request
            else if (error.data?.message != null) {
                dispatch(showErrorToaster(error.data?.message));
            }
        }
    }

    useEffect(() => {
        handleClear();
    }, [openPopup]);

    return (
        <Dialog
            open={openPopup}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            sx={{
                zoom: matchDown2 ? '0.8' :
                      matchDown1 ? '0.9' :
                      1
            }}
        >
             <DialogTitle
                sx={{
                    fontSize: "1rem",
                    pt: 4,
                }}
            >
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 20,
                    }}
                >
                    <Close />
                </IconButton>
             
            </DialogTitle>

            <DialogContent sx={{ borderTop: 0 }}>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <div style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <img
                                src={referCharityImg}
                                alt="img"
                                height={140}
                            />
                        </div>
                    </Grid>
                </Grid>

                <Grid container spacing={2} mt={2} mb={3} textAlign="center">
                    <Grid item xs={12}>
                       <span style={{ fontWeight: 'bold', fontSize: '19px' }}>{t('labels.you_got_funded')}</span>
                    </Grid>
                    <Grid item xs={12} >
                       <Typography variant="label" fontSize={18}>{t('labels.report_your_successful_grant')}</Typography>
                    </Grid>
                </Grid>

                <Grid container spacing={2} mb={3} textAlign="center">
                    <Grid item xs={12} >
                       <Typography variant="body1">{t('messages.congratulations_on_securing_funding')}</Typography>
                    </Grid>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                       <div style={{ display: 'flex', alignItems: 'center' }}>
                            <TaskAltOutlined color="secondary" />
                            <span style={{ marginLeft: '10px' }}>{t('messages.report_grant_bonus_1')}</span>
                        </div>
                    </Grid>

                    <Grid item xs={12}>
                       <div style={{ display: 'flex', alignItems: 'center' }}>
                            <TaskAltOutlined color="secondary" />
                            <span style={{ marginLeft: '10px' }}>{t('messages.report_grant_bonus_2')}</span>
                        </div>
                    </Grid>

                    <Grid item xs={12}>
                       <div style={{ display: 'flex', alignItems: 'center' }}>
                            <TaskAltOutlined color="secondary" />
                            <span style={{ marginLeft: '10px' }}>{t('messages.report_grant_bonus_3')}</span>
                        </div>
                    </Grid>
                </Grid>

                <Grid container spacing={2} mt={2} textAlign="center">
                    <Grid item xs={12} >
                       <Typography variant="body1">{t('messages.it_takes_less_than_a_minute')}</Typography>
                    </Grid>
                </Grid>

                <Grid container spacing={2} mt={1}>
                    <Grid item xs={6}>
                      <FormControlWrapper 
                            value={formFields.grantAmount.value}
                            label={t('labels.grant_amount')}
                            type="number"
                            error={formFields.grantAmount.error}
                            theme={theme}
                            required
                            onChange={(e) => handleUpdate("grantAmount", e.target.value)}
                            maskNumber
                            formControlSx={{
                                "label[data-shrink='false']": {
                                    top: '-5px'
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                       <FormControlWrapper 
                            value={formFields.grantPurpose.value}
                            label={t('labels.purpose')}
                            error={formFields.grantPurpose.error}
                            theme={theme}
                            required
                            onChange={(e) => handleUpdate("grantPurpose", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={6} mt={-2}>
                        <DatePickerWrapper 
                            label={t('labels.date_awarded')}
                            value={formFields.dateAwarded.value}
                            error={formFields.dateAwarded.error}
                            theme={theme}
                            onChange={(date) => handleUpdate("dateAwarded", date.toString())}
                        />
                    </Grid>

                </Grid>

               <Grid container spacing={2}>
                    <Grid item xs={12} mt={2} sx={{
                        '.MuiSvgIcon-root': {
                            color: formFields.consent.error !== '' ? theme.palette.error.main : 'inherit'
                        }
                    }}>
                        <CheckboxWrapper 
                            name="consent"
                            label={t('messages.consent_to_allowing')}
                            checked={formFields.consent.value}
                            onChange={(element) => handleUpdate("consent", element.target.checked)}
                            variant="label"
                            color={formFields.consent.error !== '' ? 'error' : 'primary'}
                        />
                        {formFields.consent.error !== '' && (
                            <FormHelperText error sx={{textAlign: 'center'}}>
                                {t(formFields.consent.error)}
                            </FormHelperText>
                        )}
                      
                    </Grid>

                    <Grid item xs={12} textAlign="center">
                        <LoadingButton
                            variant="contained"
                            color="secondary"
                            size="large"
                            loading={isSubmiting}
                            onClick={handleSubmit}
                        >
                            {t('buttons.claim_my_credits')}
                        </LoadingButton>
                    </Grid>
                </Grid>

            </DialogContent>

        </Dialog>
    );
};

export default ReportGrantPopup;
